import moment from 'moment';

/**
* Helpers for the external client API keys UI (VD-725).
* The API never returns a status field, the dashboard derives it from the
* timestamps on the key document.
*/

const DAY = 24 * 60 * 60 * 1000;

/** A key whose expiresAt falls inside this window gets an "expires soon" warning */
export const EXPIRY_WARNING_DAYS = 7;

export const API_KEY_STATUS = {
    REVOKED: 'revoked',
    GRACE: 'grace',
    EXPIRED: 'expired',
    DISABLED: 'disabled',
    ACTIVE: 'active',
};

export const toTime = (value) => {
    if (!value) {
        return null;
    }

    let time = new Date(value).getTime();

    return isNaN(time) ? null : time;
};

export const formatDateTime = (value, fallback = '-') => {
    if (!value) {
        return fallback;
    }

    let date = moment(value);

    return date.isValid() ? date.format('YYYY-MM-DD HH:mm') : String(value);
};

export const formatDate = (value, fallback = '-') => {
    if (!value) {
        return fallback;
    }

    let date = moment(value);

    return date.isValid() ? date.format('YYYY-MM-DD') : String(value);
};

/**
* Masked representation of a key, the plain key is only ever shown once right
* after it is created or rotated.
*/
export const maskApiKey = (item) => `vd_…_${item && item.prefix ? item.prefix : '????????'}_••••`;

/**
* A revoked key cannot be enabled or rotated any more. A key that is inside its
* rotation grace period (revokedAt in the future) is already revoked as well,
* it just keeps working until that moment.
*/
export const isApiKeyRevoked = (item) => !!(item && item.revokedAt);

/**
* Status of a key, resolved in the order defined by VD-725.
* @param item  key document as returned by the API
* @param lang  current dashboard language
* @param now   current time in ms, injectable for tests
* @returns {{code: string, label: string, className: string, note: ?string}}
*/
export const getApiKeyStatus = (item, lang, now = Date.now()) => {
    let revokedAt = toTime(item && item.revokedAt);
    let expiresAt = toTime(item && item.expiresAt);

    if (revokedAt && revokedAt <= now) {
        return {
            code: API_KEY_STATUS.REVOKED,
            label: item && item.rotatedTo
                ? `${'Revoked'.translate(lang)} (${'rotated'.translate(lang)})`
                : 'Revoked'.translate(lang),
            className: 'api-key-badge api-key-badge-revoked',
            note: null,
        };
    }

    if (revokedAt && revokedAt > now) {
        return {
            code: API_KEY_STATUS.GRACE,
            label: 'Expiring'.translate(lang),
            className: 'api-key-badge api-key-badge-grace',
            note: `${'The old key keeps working until'.translate(lang)} ${formatDateTime(item.revokedAt)}`,
        };
    }

    if (expiresAt && expiresAt <= now) {
        return {
            code: API_KEY_STATUS.EXPIRED,
            label: 'Expired'.translate(lang),
            className: 'api-key-badge api-key-badge-expired',
            note: null,
        };
    }

    if (item && item.isEnabled === false) {
        return {
            code: API_KEY_STATUS.DISABLED,
            label: 'Inactive'.translate(lang),
            className: 'api-key-badge api-key-badge-disabled',
            note: null,
        };
    }

    let note = null;

    if (expiresAt) {
        let days = Math.ceil((expiresAt - now) / DAY);

        if (days <= EXPIRY_WARNING_DAYS) {
            note = days <= 1
                ? 'expires today'.translate(lang)
                : `${'expires in'.translate(lang)} ${days} ${'days'.translate(lang)}`;
        }
    }

    return {
        code: API_KEY_STATUS.ACTIVE,
        label: 'Active'.translate(lang),
        className: 'api-key-badge api-key-badge-active',
        note: note,
    };
};

const IPV4_OCTET = '(25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9]?[0-9])';
const IPV4 = `(${IPV4_OCTET}\\.){3}${IPV4_OCTET}`;
const IPV6 = [
    '([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}',
    '([0-9a-fA-F]{1,4}:){1,7}:',
    '([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}',
    '([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}',
    '([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}',
    '([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}',
    '([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}',
    '[0-9a-fA-F]{1,4}:(:[0-9a-fA-F]{1,4}){1,6}',
    ':((:[0-9a-fA-F]{1,4}){1,7}|:)',
    `::(ffff(:0{1,4})?:)?${IPV4}`,
    `([0-9a-fA-F]{1,4}:){1,4}:${IPV4}`,
].join('|');

const IPV4_RE = new RegExp(`^${IPV4}$`);
const IPV4_CIDR_RE = new RegExp(`^${IPV4}/(3[0-2]|[12]?[0-9])$`);
const IPV6_RE = new RegExp(`^(${IPV6})$`);
const IPV6_CIDR_RE = new RegExp(`^(${IPV6})/(12[0-8]|1[01][0-9]|[1-9]?[0-9])$`);

/**
* Basic client side check for a single whitelist entry. The backend does the
* authoritative validation and answers with 400 and the list of bad entries.
*/
export const isValidIpWhitelistEntry = (value) => {
    if (!value) {
        return false;
    }

    let entry = String(value).trim();

    return IPV4_RE.test(entry) || IPV4_CIDR_RE.test(entry) || IPV6_RE.test(entry) || IPV6_CIDR_RE.test(entry);
};
